import { useEffect, useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  FileButton,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { patchCachedUserProfile } from '../../hooks/useAppUser';
import {
  removeProfilePhoto,
  updateDisplayName,
  updateProfilePhoto,
} from '../../services/userIdentityService';
import { prepareAvatarImage, PreparedAvatar } from '../../utils/avatarImage';
import { EditableAvatar } from './EditableAvatar';
import { patchCachedPublicProfileOwner } from '../../utils/publicProfileCache';

/** Matches the server's cap, so the input can't submit a name it would trim. */
export const MAX_DISPLAY_NAME_LENGTH = 40;

type ProfileIdentityModalProps = {
  opened: boolean;
  onClose: () => void;
  displayName?: string;
  photoURL?: string;
  /**
   * Saved values, for a page holding its own copy of them (the public
   * profile's owner block). The shared user-profile cache is updated here, so
   * consumers reading useAppUser need no callback.
   */
  onSaved?: (identity: { displayName?: string; photoURL?: string }) => void;
};

/**
 * Editor for the name and photo shown on a user's public cookbook.
 *
 * Accounts created with an email and password have neither — their sign-in
 * provider supplies no name or picture — so they render as "User" behind an
 * empty avatar until they set them here.
 */
export const ProfileIdentityModal = ({
  opened,
  onClose,
  displayName,
  photoURL,
  onSaved,
}: ProfileIdentityModalProps) => {
  const { user } = useAuth();
  const [name, setName] = useState(displayName ?? '');
  // A photo picked but not yet uploaded: nothing leaves the browser until Save.
  const [pendingPhoto, setPendingPhoto] = useState<PreparedAvatar | null>(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reopening starts from what's currently saved, not from an abandoned edit.
  useEffect(() => {
    if (opened) {
      setName(displayName ?? '');
      setPendingPhoto(null);
      setPhotoRemoved(false);
    }
  }, [opened, displayName]);

  const previewPhoto = pendingPhoto?.previewUrl ?? (photoRemoved ? undefined : photoURL);
  const trimmedName = name.trim();
  const nameChanged = trimmedName !== (displayName ?? '').trim();
  const photoChanged = !!pendingPhoto || (photoRemoved && !!photoURL);
  const hasChanges = nameChanged || photoChanged;

  const handlePick = async (file: File | null) => {
    if (!file) return;
    try {
      setPendingPhoto(await prepareAvatarImage(file));
      setPhotoRemoved(false);
    } catch (err) {
      notifications.show({
        title: "Couldn't use that photo",
        message: err instanceof Error ? err.message : 'Please try another image.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    }
  };

  const handleSave = async () => {
    if (!user || !hasChanges) return;
    setSaving(true);
    try {
      const token = await user.getIdToken();
      const saved: { displayName?: string; photoURL?: string } = {
        displayName: displayName,
        photoURL: photoURL,
      };

      // Photo first: if the name save fails, the user still sees which half
      // landed rather than an all-or-nothing failure with a changed avatar.
      if (pendingPhoto) {
        const result = await updateProfilePhoto(
          pendingPhoto.imageData,
          pendingPhoto.contentType,
          token
        );
        saved.photoURL = result.photoURL;
      } else if (photoRemoved && photoURL) {
        await removeProfilePhoto(token);
        saved.photoURL = undefined;
      }

      if (nameChanged) {
        const result = await updateDisplayName(trimmedName || null, token);
        saved.displayName = result.displayName ?? undefined;
      }

      patchCachedUserProfile(user.uid, {
        displayName: saved.displayName,
        photoURL: saved.photoURL,
      });
      // The user's own public cookbook renders this too, and may already be
      // seeded with the old values from an earlier visit this session.
      patchCachedPublicProfileOwner(user.uid, {
        displayName: saved.displayName,
        photoURL: saved.photoURL,
      });
      onSaved?.(saved);

      notifications.show({
        title: 'Profile updated',
        message: 'Your name and photo are what others will see.',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      onClose();
    } catch {
      notifications.show({
        title: "Couldn't save your profile",
        message: 'Something went wrong. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Name and photo"
      radius="lg"
      centered
    >
      <Stack gap="lg">
        <Text size="sm" c="dimmed">
          This is how you appear on your public cookbook and on recipes you share.
        </Text>

        {/* The avatar is the control: tapping your own photo to change it is
            the gesture people arrive with, and it leaves one quiet secondary
            action instead of two competing buttons. */}
        <Stack gap={6} align="center">
          <FileButton onChange={handlePick} accept="image/*">
            {(props) => (
              <EditableAvatar
                photoURL={previewPhoto}
                displayName={trimmedName}
                size={96}
                badge="camera"
                label={previewPhoto ? 'Change photo' : 'Add a photo'}
                onClick={props.onClick}
              />
            )}
          </FileButton>
          {/* Reserved height: the dialog must not jump as Remove comes and
              goes with the photo. */}
          <Box h={22}>
            {previewPhoto && (
              <Anchor
                component="button"
                type="button"
                size="xs"
                c="dimmed"
                onClick={() => {
                  setPendingPhoto(null);
                  setPhotoRemoved(true);
                }}
              >
                Remove photo
              </Anchor>
            )}
          </Box>
        </Stack>

        <TextInput
          label="Display name"
          placeholder="Your name"
          value={name}
          maxLength={MAX_DISPLAY_NAME_LENGTH}
          onChange={(event) => setName(event.currentTarget.value)}
          description="Leave this empty to show no name."
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="orange" onClick={handleSave} loading={saving} disabled={!hasChanges}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
